import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLoadMore } from './use-load-more';
import type { FetchFiltered, FetchPage } from '../hub-pages';
import { HUB_PAGE_SIZE } from '../list-queries';

type FilteredState<T, F> = {
  filters: F;
  rows: T[];
  total: number | null;
  hasMore: boolean;
};

/**
 * The /tours and /boats hubs' card list: the baked seed + "Show more" while
 * every filter is at its default, the filtered RPC once any isn't.
 *
 * The seed is the unfiltered first page, so a filtered hub can't start from
 * it — the first filtered page (with its exact total) is fetched in the
 * browser, and "Show more" then pages that same query. Until it lands the
 * previous rows stay on screen (`loading`), so the grid doesn't collapse.
 * Static HTML is always the default state: filters exist only after hydration.
 */
export function useFilteredHub<T extends { id: string }, F>(
  seed: T[],
  fetchSeedPage: FetchPage<T>,
  filters: F,
  isDefault: boolean,
  fetchFiltered: FetchFiltered<T, F>,
) {
  const [state, setState] = useState<FilteredState<T, F> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const request = useRef(0);

  useEffect(() => {
    const id = ++request.current;
    if (isDefault) {
      setState(null);
      setLoading(false);
      setError(false);
      return;
    }
    setLoading(true);
    setError(false);
    fetchFiltered(filters, 0, HUB_PAGE_SIZE + 1, true)
      .then(({ rows, total }) => {
        if (id !== request.current) return;
        setState({
          filters,
          rows: rows.slice(0, HUB_PAGE_SIZE),
          total,
          hasMore: rows.length > HUB_PAGE_SIZE,
        });
      })
      .catch(() => {
        if (id === request.current) setError(true);
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [filters, isDefault, fetchFiltered, attempt]);

  // "Show more" pages the query whose rows are on screen, not the filters
  // just picked (those are still loading).
  const shownFilters = state?.filters;
  const fetchPage = useMemo<FetchPage<T>>(
    () =>
      shownFilters === undefined
        ? fetchSeedPage
        : (offset, count) =>
            fetchFiltered(shownFilters, offset, count, false).then((page) => page.rows),
    [shownFilters, fetchSeedPage, fetchFiltered],
  );

  const rows = state ? state.rows : seed;
  const more = useLoadMore(rows, fetchPage, HUB_PAGE_SIZE, state ? state.hasMore : true);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    /** The first page on screen: the seed, or the latest filtered page. */
    rows,
    /** Rows appended by "Show more". */
    more,
    /** Whether the rows on screen are a filtered result. */
    filtered: state != null,
    /** Matches for the filtered rows on screen; null for the seed. */
    total: state?.total ?? null,
    /** A new filtered first page is on its way; the rows on screen are stale. */
    loading,
    /** The latest filtered first page failed; `retry` asks again. */
    error,
    retry,
  };
}
